/**
 * Piezas visuales del módulo «Uso de la app». Todas leen colores de las variables
 * del módulo (--serie-ios, --serie-android, …) para que el modo oscuro cambie en un
 * solo lugar, y ninguna depende de los datos del servidor más allá de lo que recibe.
 */
import { useId, useState, type ReactNode } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BarChart3, Table2 } from 'lucide-react';
import styles from './UsoAppModule.module.css';
import { nf, SERIE_ANDROID, SERIE_IOS, SERIE_UNICA } from './usoAppFormato';

const pct = (n: number) => `${Math.round(n)}%`;

/* ── Minigráfica de tendencia (sin ejes) ───────────────────────────────── */

export function Sparkline({ valores, etiqueta }: { valores: number[]; etiqueta: string }) {
  const id = useId();
  if (valores.length < 2 || valores.every((v) => v === 0)) {
    return <div className={styles.sparkVacia} aria-hidden="true" />;
  }
  const w = 120, h = 34, max = Math.max(...valores, 1);
  const pts = valores.map((v, i) => [
    (i / (valores.length - 1)) * w,
    h - 3 - (v / max) * (h - 6),
  ] as const);
  const linea = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${linea} L${w},${h} L0,${h} Z`;
  const [ux, uy] = pts[pts.length - 1];
  return (
    <svg className={styles.spark} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" role="img" aria-label={etiqueta}>
      <defs>
        <linearGradient id={`g${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--spark-tono)" stopOpacity="0.22" />
          <stop offset="100%" stopColor="var(--spark-tono)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#g${id})`} />
      <path d={linea} fill="none" stroke="var(--spark-tono)" strokeWidth="1.6" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
      <circle cx={ux} cy={uy} r="2.6" fill="var(--spark-tono)" />
    </svg>
  );
}

/* ── Barra iOS / Android con porcentajes ───────────────────────────────── */

export function BarraPlataformas({ ios, android, compacta = false }: { ios: number; android: number; compacta?: boolean }) {
  const total = ios + android;
  if (total === 0) return <div className={styles.splitVacio}>Sin datos por plataforma</div>;
  const pa = (android / total) * 100;
  return (
    <div className={compacta ? styles.splitCompacto : styles.split}>
      <div className={styles.splitPista} role="img"
        aria-label={`Android ${pct(pa)}, iOS ${pct(100 - pa)}`}>
        <span style={{ width: `${pa}%`, background: SERIE_ANDROID }} />
        <span style={{ width: `${100 - pa}%`, background: SERIE_IOS }} />
      </div>
      {!compacta && (
        <div className={styles.splitLeyenda}>
          <span><i style={{ background: SERIE_ANDROID }} />Android <b>{pct(pa)}</b> <em>{nf.format(android)}</em></span>
          <span><i style={{ background: SERIE_IOS }} />iOS <b>{pct(100 - pa)}</b> <em>{nf.format(ios)}</em></span>
        </div>
      )}
    </div>
  );
}

/* ── Escala anidada (hoy ⊂ semana ⊂ mes, o 24 h ⊂ 7 d ⊂ 30 d ⊂ cuentas) ── */

export interface Escalon { etiqueta: string; valor: number; nota?: string }

/**
 * Cada escalón es un subconjunto del siguiente, así que se dibujan superpuestos
 * desde el mismo origen: se lee de un vistazo qué parte del grupo grande volvió
 * en la ventana corta. El último escalón es la referencia (100 %).
 */
export function EscalaAnidada({ escalones, unidad }: { escalones: Escalon[]; unidad: string }) {
  const base = Math.max(1, escalones[escalones.length - 1]?.valor ?? 1);
  const tonos = ['var(--escala-1)', 'var(--escala-2)', 'var(--escala-3)', 'var(--escala-4)'];
  const desdeAtras = escalones.map((e, i) => ({ ...e, tono: tonos[tonos.length - escalones.length + i] })).reverse();
  return (
    <div className={styles.escala}>
      <div className={styles.escalaPista} role="img"
        aria-label={escalones.map((e) => `${e.etiqueta}: ${nf.format(e.valor)} ${unidad}`).join('; ')}>
        {desdeAtras.map((e) => (
          <span key={e.etiqueta} className={styles.escalaBarra}
            style={{ width: e.valor > 0 ? `${Math.max(1.5, (e.valor / base) * 100)}%` : 0, background: e.tono }} />
        ))}
      </div>
      <ul className={styles.escalaLeyenda}>
        {escalones.map((e, i) => (
          <li key={e.etiqueta}>
            <i style={{ background: tonos[tonos.length - escalones.length + i] }} />
            <span>{e.etiqueta}</span>
            <b>{nf.format(e.valor)}</b>
            {i < escalones.length - 1 && <em>{pct((e.valor / base) * 100)}</em>}
            {e.nota && <small>{e.nota}</small>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Indicador con minigráfica ─────────────────────────────────────────── */

export function Indicador({ etiqueta, valor, pie, serie, tono, children }: {
  etiqueta: string; valor: number | null; pie?: ReactNode; serie?: number[]; tono: string; children?: ReactNode;
}) {
  return (
    <div className={styles.indicador} style={{ ['--spark-tono' as string]: tono }}>
      <div className={styles.indicadorEtiqueta}>{etiqueta}</div>
      <div className={styles.indicadorFila}>
        <div className={styles.indicadorValor}>{valor === null ? '—' : nf.format(valor)}</div>
        {serie && <Sparkline valores={serie} etiqueta={`Tendencia de ${etiqueta.toLowerCase()}`} />}
      </div>
      {pie && <div className={styles.indicadorPie}>{pie}</div>}
      {children}
    </div>
  );
}

/* ── Gráfica principal con pestañas por métrica ────────────────────────── */

type Punto = { etiqueta: string } & Record<string, number | string>;
export interface Metrica {
  clave: string;
  nombre: string;
  descripcion: string;
  datos: Punto[];
  /** true = una sola serie (sin desglose por plataforma). */
  unica?: boolean;
}

function TooltipUso({ active, payload, label }:
  { active?: boolean; payload?: Array<{ name?: string; value?: number; color?: string }>; label?: string }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + Number(p.value ?? 0), 0);
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipTitulo}>{label}</div>
      {payload.length > 1 && payload.map((p, i) => (
        <div key={i} className={styles.tooltipFila}>
          <span className={styles.tooltipPunto} style={{ background: p.color }} />
          <span className={styles.tooltipNombre}>{p.name}</span>
          <b className={styles.tooltipValor}>{nf.format(Number(p.value ?? 0))}</b>
        </div>
      ))}
      <div className={`${styles.tooltipFila} ${styles.tooltipTotal}`}>
        <span className={styles.tooltipNombre}>Total</span>
        <b className={styles.tooltipValor}>{nf.format(total)}</b>
      </div>
    </div>
  );
}

export function GraficaTendencia({ metricas }: { metricas: Metrica[] }) {
  const [activa, setActiva] = useState(metricas[0]?.clave);
  const [vista, setVista] = useState<'grafica' | 'tabla'>('grafica');
  const tabId = useId();
  const m = metricas.find((x) => x.clave === activa) ?? metricas[0];
  if (!m) return null;
  const totales = m.datos.reduce((acc, d) => ({
    ios: acc.ios + Number(d.ios ?? 0),
    android: acc.android + Number(d.android ?? 0),
    unica: acc.unica + Number(d.valor ?? 0),
  }), { ios: 0, android: 0, unica: 0 });
  const hay = m.unica ? totales.unica > 0 : totales.ios + totales.android > 0;

  return (
    <section className={`${styles.panel} ${styles.tendencia}`} aria-labelledby={`${tabId}-t`}>
      <header className={styles.panelCabeza}>
        <div>
          <h2 id={`${tabId}-t`} className={styles.panelTitulo}>Tendencia</h2>
          <p className={styles.panelNota}>{m.descripcion}</p>
        </div>
        <div className={styles.vistaToggle} role="group" aria-label="Ver como">
          <button type="button" aria-pressed={vista === 'grafica'} onClick={() => setVista('grafica')} title="Gráfica"><BarChart3 size={15} /> Gráfica</button>
          <button type="button" aria-pressed={vista === 'tabla'} onClick={() => setVista('tabla')} title="Tabla"><Table2 size={15} /> Tabla</button>
        </div>
      </header>

      <div className={styles.metricaTabs} role="tablist" aria-label="Métrica">
        {metricas.map((x) => (
          <button key={x.clave} type="button" role="tab" aria-selected={x.clave === m.clave}
            className={styles.metricaTab} onClick={() => setActiva(x.clave)}>
            {x.nombre}
          </button>
        ))}
      </div>

      {!hay ? (
        <div className={styles.graficaVacia}>Sin datos de «{m.nombre.toLowerCase()}» en el período.</div>
      ) : vista === 'grafica' ? (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={m.datos} margin={{ top: 12, right: 8, left: -12, bottom: 0 }} barCategoryGap="22%">
              <CartesianGrid stroke="var(--color-border-soft)" vertical={false} />
              <XAxis dataKey="etiqueta" fontSize={11} tickLine={false} axisLine={false} tick={{ fill: 'var(--color-text-tertiary)' }} minTickGap={12} />
              <YAxis fontSize={11} allowDecimals={false} tickLine={false} axisLine={false} width={48} tick={{ fill: 'var(--color-text-tertiary)' }}
                tickFormatter={(v: number) => nf.format(v)} />
              <Tooltip content={<TooltipUso />} cursor={{ fill: 'var(--color-primary-soft)' }} />
              {m.unica ? (
                <Bar dataKey="valor" name={m.nombre} fill={SERIE_UNICA} radius={[4, 4, 0, 0]} maxBarSize={42} />
              ) : (
                <>
                  <Bar dataKey="android" name="Android" stackId="p" fill={SERIE_ANDROID} maxBarSize={42}
                    stroke="var(--color-surface)" strokeWidth={1} />
                  <Bar dataKey="ios" name="iOS" stackId="p" fill={SERIE_IOS} radius={[4, 4, 0, 0]} maxBarSize={42}
                    stroke="var(--color-surface)" strokeWidth={1} />
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
          <div className={styles.tendenciaPie}>
            {m.unica ? (
              <span className={styles.totalPeriodo}>Total del período <b>{nf.format(totales.unica)}</b></span>
            ) : (
              <BarraPlataformas ios={totales.ios} android={totales.android} />
            )}
          </div>
        </>
      ) : (
        <div className={styles.tablaScroll}>
          <table className={styles.tabla}>
            <caption className={styles.srOnly}>{m.nombre} por período</caption>
            <thead>
              <tr>
                <th scope="col">Período</th>
                {!m.unica && <th scope="col" className={styles.num}>Android</th>}
                {!m.unica && <th scope="col" className={styles.num}>iOS</th>}
                <th scope="col" className={styles.num}>Total</th>
              </tr>
            </thead>
            <tbody>
              {m.datos.map((d) => {
                const t = m.unica ? Number(d.valor ?? 0) : Number(d.ios ?? 0) + Number(d.android ?? 0);
                return (
                  <tr key={String(d.etiqueta)}>
                    <td>{d.etiqueta}</td>
                    {!m.unica && <td className={styles.num}>{nf.format(Number(d.android ?? 0))}</td>}
                    {!m.unica && <td className={styles.num}>{nf.format(Number(d.ios ?? 0))}</td>}
                    <td className={styles.num}><b>{nf.format(t)}</b></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ── Embudo de pasos ───────────────────────────────────────────────────── */

export interface Paso { etiqueta: string; valor: number }

/**
 * Pasos de un recorrido. Son conteos de eventos (no personas distintas), así que
 * el porcentaje se calcula contra el primer paso y se ofrece también la caída
 * respecto al paso anterior, que es lo que dice dónde se pierde la gente.
 */
export function Embudo({ pasos }: { pasos: Paso[] }) {
  const base = Math.max(1, pasos[0]?.valor ?? 1);
  if (pasos.every((p) => p.valor === 0)) return <div className={styles.graficaVacia}>Sin actividad en el período.</div>;
  return (
    <ol className={styles.embudo}>
      {pasos.map((p, i) => {
        const anterior = i > 0 ? pasos[i - 1].valor : 0;
        const conv = i > 0 && anterior > 0 ? (p.valor / anterior) * 100 : null;
        return (
          <li key={p.etiqueta} className={styles.embudoPaso}>
            <div className={styles.embudoTexto}>
              <span className={styles.embudoEtiqueta}>{p.etiqueta}</span>
              <span className={styles.embudoCifras}>
                <b>{nf.format(p.valor)}</b>
                {conv !== null && (
                  <em className={conv < 50 ? styles.embudoCaida : undefined}
                    title={`${pct(conv)} respecto al paso anterior`}>
                    {pct(conv)} del paso anterior
                  </em>
                )}
              </span>
            </div>
            <div className={styles.embudoPista}>
              <span style={{ width: `${Math.max(1, Math.min(100, (p.valor / base) * 100))}%`, opacity: 1 - i * 0.14 }} />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/* ── Resultados por estado (aprobado / revisión / rechazado) ───────────── */

export function Resultados({ items }: { items: Array<{ etiqueta: string; valor: number; tono: 'ok' | 'warn' | 'err'; icono: ReactNode }> }) {
  const total = items.reduce((s, x) => s + x.valor, 0);
  if (total === 0) return <div className={styles.graficaVacia}>Sin resultados en el período.</div>;
  return (
    <div className={styles.resultados}>
      <div className={styles.resultadosPista} role="img"
        aria-label={items.map((x) => `${x.etiqueta}: ${nf.format(x.valor)}`).join('; ')}>
        {items.filter((x) => x.valor > 0).map((x) => (
          <span key={x.etiqueta} className={styles[`tono_${x.tono}`]} style={{ width: `${(x.valor / total) * 100}%` }} />
        ))}
      </div>
      <ul className={styles.resultadosLista}>
        {items.map((x) => (
          <li key={x.etiqueta} className={styles[`tono_${x.tono}`]}>
            <span className={styles.resultadoIcono}>{x.icono}</span>
            <span>{x.etiqueta}</span>
            <b>{nf.format(x.valor)}</b>
            <em>{pct((x.valor / total) * 100)}</em>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Ranking con desglose por plataforma ───────────────────────────────── */

export function Ranking({ filas, inicial = 8 }: {
  filas: Array<{ clave: string; nombre: string; titulo?: string; ios: number; android: number }>;
  inicial?: number;
}) {
  const [todas, setTodas] = useState(false);
  filas = [...filas].sort((a, b) => (b.ios + b.android) - (a.ios + a.android));
  const total = filas.reduce((s, f) => s + f.ios + f.android, 0);
  const max = Math.max(1, ...filas.map((f) => f.ios + f.android));
  const visibles = todas ? filas : filas.slice(0, inicial);
  return (
    <>
      <ol className={styles.ranking}>
        {visibles.map((f, i) => {
          const t = f.ios + f.android;
          return (
            <li key={f.clave} className={styles.rankingFila}
              title={`${f.nombre}: ${nf.format(t)} (Android ${nf.format(f.android)} · iOS ${nf.format(f.ios)})`}>
              <span className={styles.rankingPos}>{i + 1}</span>
              <span className={styles.rankingNombre} title={f.titulo}>{f.nombre}</span>
              <span className={styles.rankingValor}>{nf.format(t)} <em>{pct(total ? (t / total) * 100 : 0)}</em></span>
              <span className={styles.rankingPista}>
                <span style={{ width: `${(f.android / max) * 100}%`, background: SERIE_ANDROID }} />
                <span style={{ width: `${(f.ios / max) * 100}%`, background: SERIE_IOS }} />
              </span>
            </li>
          );
        })}
      </ol>
      {filas.length > inicial && (
        <button type="button" className={styles.verMas} onClick={() => setTodas((v) => !v)} aria-expanded={todas}>
          {todas ? 'Ver menos' : `Ver las ${filas.length}`}
        </button>
      )}
    </>
  );
}

/* ── Panel genérico ────────────────────────────────────────────────────── */

export function Panel({ titulo, nota, icono, children, className, accion }: {
  titulo: string; nota?: ReactNode; icono?: ReactNode; children: ReactNode; className?: string; accion?: ReactNode;
}) {
  const id = useId();
  return (
    <section className={`${styles.panel} ${className ?? ''}`} aria-labelledby={id}>
      <header className={styles.panelCabeza}>
        <div className={styles.panelTituloFila}>
          {icono && <span className={styles.panelIcono} aria-hidden="true">{icono}</span>}
          <div>
            <h2 id={id} className={styles.panelTitulo}>{titulo}</h2>
            {nota && <p className={styles.panelNota}>{nota}</p>}
          </div>
        </div>
        {accion}
      </header>
      {children}
    </section>
  );
}
